import ModalDeletion from '../ui/ModalDeletion';
import BenefitDeletionAlertModal from './BenefitDeletionAlertModal';
import {
  useDeleteBenefit,
  useGetCircuitsUsingBenefit,
} from '@/hooks/useBenefits';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/i18n';

interface BenefitDeletionModalProps {
  benefitId: string;
  closeModal: () => void;
  /** Called once the benefit is in the trash, typically to leave the form. */
  onDeleted: () => void;
}

/**
 * Deletion of a single benefit from its form. Shows the red warning when the
 * benefit is used in a circuit or associated with another benefit, since the
 * deletion removes it from there, and the usual confirmation otherwise.
 */
const BenefitDeletionModal = ({
  benefitId,
  closeModal,
  onDeleted,
}: BenefitDeletionModalProps) => {
  const { t } = useTranslation();
  const { toastAsync } = useToast();
  const { mutateAsync: deleteBenefit } = useDeleteBenefit();
  const {
    data: usages,
    isPending,
    isError,
  } = useGetCircuitsUsingBenefit(benefitId);

  const deleteAndLeave = () =>
    toastAsync(deleteBenefit({ benefitId }), {
      success: t('trash.movedToTrash.single'),
    }).then(onDeleted);

  if (isPending) return null;

  // Without the usages, err on the side of the warning.
  const isUsedElsewhere =
    isError || usages.circuits.length > 0 || usages.hotels.length > 0;

  return isUsedElsewhere ? (
    <BenefitDeletionAlertModal
      closeModal={closeModal}
      actionOnClick={deleteAndLeave}
    />
  ) : (
    <ModalDeletion
      closeModal={closeModal}
      actionOnClick={deleteAndLeave}
      type="prestation"
    />
  );
};

export default BenefitDeletionModal;
